/**
 * RoleOptimizePage — pSEO landing for "[role] resume optimization" intent.
 *
 * Distinct from /resume/:slug (which targets "[role] resume examples"
 * search intent). This page targets users actively trying to OPTIMIZE
 * a resume for a specific role, not browse examples. Different keyword
 * intent, different content, different canonical — both pages co-exist
 * cleanly and target separate SERP queries.
 *
 * Initial slugs (priority 1, defined in OPTIMIZE_ROLES):
 *   /optimize/software-engineer-resume
 *   /optimize/registered-nurse-resume
 *   /optimize/data-analyst-resume
 *
 * Page anatomy (built for crawlability + conversion):
 *   - <h1> + sub-headline with the role's exact keyword
 *   - 3-bullet "what ATS rejects" pain block
 *   - Role-specific keyword pool (visible, crawlable, scannable)
 *   - "Optimize My Resume — Free" primary CTA → /free-ats-check
 *   - 3-step "how it works" mini-flow
 *   - FAQ with 3 role-tailored questions (rich-result eligible)
 *
 * SEO metadata is set BOTH at build time (seo-routes.js → prerender.js
 * static HTML) AND at runtime via useDocumentMeta — the latter covers
 * preview env and any SPA-fallback edge cases. Canonical is NOT written
 * client-side; the prerender owns it.
 */
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, AlertTriangle, Target, FileText, Sparkles } from 'lucide-react';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { OPTIMIZE_ROLES } from '../data/optimizeRoles';
import OptimizeCrosslinkRail from '../components/OptimizeCrosslinkRail';

// The data module exports `OPTIMIZE_ROLES` as the single source of truth.
// To add a new role, edit /app/frontend/src/data/optimizeRoles.js and add
// the slug to /app/frontend/seo-routes.js + /app/frontend/public/sitemap.xml.

const CANONICAL_BASE = 'https://ascendforyou.net';
const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };
const Section = ({ children, className = '' }) => (
  <section className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const RoleOptimizePage = () => {
  const { slug } = useParams();
  const data = OPTIMIZE_ROLES[slug];

  useDocumentMeta({
    title: data ? `${data.role} Resume Optimization — Free ATS Checker | Ascend` : undefined,
    description: data
      ? `Optimize your ${data.role} resume for ATS in 60 seconds. Free keyword analysis, role-specific match scoring, and AI rewrites built for ${data.role.toLowerCase()} hiring filters.`
      : undefined,
    path: slug ? `/optimize/${slug}` : undefined,
    imageAlt: data ? `${data.role} resume optimization with ATS keyword analysis and free score` : undefined,
  });

  if (!data) return <Navigate to="/free-ats-check" replace />;

  const canonical = `${CANONICAL_BASE}/optimize/${slug}`;

  return (
    <div className="min-h-screen bg-white" data-testid={`optimize-role-${slug}`}>
      <nav className="border-b border-[#e5e7eb] bg-white sticky top-0 z-50">
        <Section className="flex items-center justify-between py-4">
          <Link to="/" className="font-satoshi text-xl font-black tracking-tight text-[#0f1115]" data-testid="logo-home">
            ASCEND
          </Link>
          <Link
            to="/free-ats-check"
            className="bg-[#002fa7] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
            data-testid="nav-cta-free-ats"
          >
            Score My Resume Free
          </Link>
        </Section>
      </nav>

      {/* Hero */}
      <Section className="py-12 sm:py-16">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eff6ff] text-[#1e3a8a] text-xs font-semibold border border-[#bfdbfe]">
            <Target className="w-3.5 h-3.5" /> {data.intent.toUpperCase()}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-satoshi font-black tracking-tight text-[#0f1115] mt-4 leading-[1.05]">
            Optimize your {data.role} resume.
            <span className="block text-[#002fa7]">In 60 seconds. Free.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#4b5563] leading-relaxed">
            Paste your resume + a real {data.role} job description. Ascend scores it against the exact ATS keywords hospital, tech, and enterprise hiring systems screen for — and shows you the gaps in plain English.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to="/free-ats-check"
              className="inline-flex items-center gap-2 bg-[#002fa7] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#001f6f] transition-colors"
              data-testid="hero-cta-free-ats"
            >
              <Sparkles className="w-4 h-4" /> Optimize My Resume — Free
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/resume/${slug.replace('-resume', '')}`}
              className="inline-flex items-center gap-2 border border-[#e5e7eb] text-[#0f1115] px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#f9fafb] transition-colors"
              data-testid="hero-secondary-cta"
            >
              See {data.role} Examples <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-4 text-xs text-[#6b7280] flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#10b981]" /> No signup · No credit card · 60 seconds
          </p>
        </motion.div>
      </Section>

      {/* Pain band */}
      <Section className="py-10 border-t border-[#e5e7eb]">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#dc2626] mb-3 block">
              WHY {data.role.toUpperCase()}S GET FILTERED OUT
            </span>
            <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] leading-tight">
              3 hidden ATS traps for {data.role} resumes.
            </h2>
          </div>
          <ul className="lg:col-span-8 space-y-4">
            {data.painBullets.map((bullet, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-4 rounded-lg border border-[#fecaca] bg-[#fef2f2]"
                data-testid={`pain-bullet-${i}`}
              >
                <AlertTriangle className="w-5 h-5 text-[#dc2626] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#7f1d1d] leading-relaxed">{bullet}</p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Keyword pool */}
      <Section className="py-12 border-t border-[#e5e7eb] bg-[#f9fafb]">
        <div className="mb-6">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
            COMMON KEYWORDS
          </span>
          <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115]">
            Keywords {data.role} JDs screen for
          </h2>
          <p className="mt-2 text-sm text-[#6b7280]">
            Sample pool — Ascend's free ATS checker scores your resume against the EXACT keywords in any job posting you paste.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="keyword-pool">
          {data.keywords.map((k) => (
            <span
              key={k}
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-[#e5e7eb] text-sm text-[#0f1115] hover:border-[#002fa7] transition-colors"
            >
              {k}
            </span>
          ))}
        </div>
      </Section>

      {/* STAR Example — Marlene v1.0 calibrated example, role-specific.
          Renders only for roles with a `starExample` payload (currently the
          3 priority pSEO routes: SWE, RN, DA). Acts as a credibility signal
          plus a copyable bullet structure the user can adapt verbatim. */}
      {data.starExample && (
        <Section className="py-12 border-t border-[#e5e7eb]">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-3 block">
                STAR EXAMPLE
              </span>
              <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] leading-tight">
                One bullet, recruiter-tested.
              </h2>
              <p className="mt-3 text-sm text-[#6b7280] leading-relaxed">
                Copy this structure into your own resume. Replace the specifics with your numbers — keep the verb order and the quantified outcome.
              </p>
            </div>
            <div className="lg:col-span-8 space-y-4" data-testid="star-example">
              {[
                ['Situation / Task', data.starExample.situation, '#fef3c7', '#92400e'],
                ['Action', data.starExample.action, '#dbeafe', '#1e40af'],
                ['Result', data.starExample.result, '#dcfce7', '#166534'],
              ].map(([label, text, bg, fg]) => (
                <div
                  key={label}
                  className="p-5 rounded-lg border border-[#e5e7eb] bg-white"
                  data-testid={`star-${label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                >
                  <span
                    className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold rounded mb-2"
                    style={{ backgroundColor: bg, color: fg }}
                  >
                    {label}
                  </span>
                  <p className="text-sm text-[#0f1115] leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* How it works */}
      <Section className="py-12 border-t border-[#e5e7eb]">
        <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-8">
          How Ascend optimizes a {data.role} resume
        </h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { step: '01', title: 'Paste resume + JD', desc: 'No upload, no signup. Just paste both into Ascend\'s free checker.' },
            { step: '02', title: 'Get your ATS score', desc: 'See keyword matches, gaps, and impact-bullet weaknesses in plain English.' },
            { step: '03', title: 'Apply the fixes', desc: 'Use Ascend\'s AI rewrite OR copy our suggestions into your own resume.' },
          ].map((s) => (
            <div key={s.step} className="border border-[#e5e7eb] rounded-lg p-6">
              <div className="text-4xl font-black text-[#e5e7eb] font-satoshi">{s.step}</div>
              <h3 className="mt-2 font-satoshi text-lg font-bold text-[#0f1115]">{s.title}</h3>
              <p className="mt-2 text-sm text-[#4b5563] leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Cost of Inaction band — $1,235/week math. Universal pain across
          all 13 pSEO roles, so it renders unconditionally. Anchored to our
          current social campaign so any visitor arriving from Facebook or
          LinkedIn sees the same framing they clicked. */}
      <Section className="py-12 border-t border-[#e5e7eb] bg-[#fef2f2]">
        <div className="max-w-3xl" data-testid="cost-of-inaction">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#dc2626] mb-3 block">
            COST OF INACTION
          </span>
          <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0f1115] leading-tight">
            Every week you stay unemployed costs ~<span className="text-[#dc2626]">$1,235</span>.
          </h2>
          <p className="mt-4 text-base text-[#4b5563] leading-relaxed">
            That's the average US mid-career professional's lost weekly wage. If a 60-second ATS check turns one ghosted application into one phone screen, you've already paid back a year of Ascend Pro. The math gets uncomfortable fast for anyone NOT running the check.
          </p>
          <Link
            to="/free-ats-check"
            className="mt-6 inline-flex items-center gap-2 bg-[#0f1115] text-white px-5 py-3 rounded-lg text-sm font-semibold hover:bg-[#dc2626] transition-colors"
            data-testid="coi-cta-free-ats"
          >
            <Sparkles className="w-4 h-4" /> Run my free check
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* FAQ — schema.org/FAQPage emitted via seo-routes.js for rich results */}
      <Section className="py-12 border-t border-[#e5e7eb] bg-[#f9fafb]">
        <h2 className="font-satoshi text-2xl sm:text-3xl font-bold text-[#0f1115] mb-8">
          {data.role} resume FAQ
        </h2>
        <div className="space-y-4 max-w-3xl">
          {data.faqs.map(([q, a], i) => (
            <details
              key={i}
              className="bg-white border border-[#e5e7eb] rounded-lg p-5 group"
              data-testid={`faq-${i}`}
            >
              <summary className="font-semibold text-[#0f1115] cursor-pointer flex items-start gap-2">
                <FileText className="w-4 h-4 text-[#002fa7] flex-shrink-0 mt-0.5" />
                {q}
              </summary>
              <p className="mt-3 text-sm text-[#4b5563] leading-relaxed pl-6">{a}</p>
            </details>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-16">
        <div className="bg-gradient-to-br from-[#0f1115] to-[#1f2937] text-white rounded-2xl p-8 sm:p-12 text-center" data-testid="cta-strip">
          <h2 className="text-2xl sm:text-3xl font-satoshi font-bold">
            Ready to optimize your {data.role} resume?
          </h2>
          <p className="mt-3 text-[#d1d5db] max-w-xl mx-auto">
            60 seconds. Free. No signup. See exactly which {data.role} keywords are missing from your resume.
          </p>
          <Link
            to="/free-ats-check"
            className="mt-6 inline-flex items-center gap-2 bg-white text-[#0f1115] px-6 py-3 rounded-lg text-sm font-bold hover:bg-[#f3f4f6] transition-colors"
            data-testid="footer-cta-free-ats"
          >
            <Sparkles className="w-4 h-4" /> Score My Resume Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>

      {/* Cross-link rail — internal SEO + UX exit ramp to peer /optimize pages */}
      <OptimizeCrosslinkRail currentSlug={slug} />

      <footer className="border-t border-[#e5e7eb] py-8">
        <Section>
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-[#6b7280]">
            <span>© 2026 Ascend. The honest AI career copilot.</span>
            <div className="flex items-center gap-4">
              <Link to="/blog" className="hover:text-[#002fa7]">Blog</Link>
              <Link to="/pricing" className="hover:text-[#002fa7]">Pricing</Link>
              <Link to="/free-ats-check" className="hover:text-[#002fa7]">Free ATS Check</Link>
            </div>
          </div>
          {/* Visible canonical hint for human auditors. Real <link rel="canonical">
              is emitted by prerender.js at build time. */}
          <p className="mt-4 text-[10px] text-[#9ca3af]">Canonical: {canonical}</p>
        </Section>
      </footer>
    </div>
  );
};

export default RoleOptimizePage;
